import type { EChartsOption } from "echarts";
import type { TanTableRenderModel } from "./tanTableSpec.js";
import type { EchartsVizSpecV1 } from "./echartsSpec.js";
import { echartsSpecToOption } from "./echartsRenderer/index.js";
import {
  findContentWidgetById,
  isNotAvailableError,
  widgetSourceKey,
  type PageWidget,
  type PageWidgetSource
} from "./pageWidgets.js";

export type GlobalColumnFilter = { column: string; values: Set<string> };

type DetailData = { columns: string[]; rows: unknown[][] };

export type PageFilterBinding =
  | { widget_id: string; kind: "column"; column: string }
  | { widget_id: string; kind: "param"; param_name: string };
export type PageFilterKind = "slicer" | "interaction";

export type PageFilter = {
  id: string;
  label: string;
  slicer_type?: "list" | "date_range";
  multi: boolean;
  /** On-page widget that drives this filter. Empty string when `source_viz` is used instead. */
  source_widget_id: string;
  /** Off-page viz node driving this filter. When set, takes precedence over `source_widget_id`. */
  source_viz?: PageWidgetSource;
  source_column: string;
  bindings: PageFilterBinding[];
  /** Default `slicer` for older saved pages. Interactions created in the builder use `interaction`. */
  filter_kind?: PageFilterKind;
  default_start?: string;
  default_end?: string;
};

export type DateRangeState = { start: string | null; end: string | null };
export type DateRangeSelections = Record<string, DateRangeState>;

export function isDateRangeSlicer(f: PageFilter): boolean {
  return f.slicer_type === "date_range";
}

/**
 * Source key used to look up a filter's driving render model in a batch-resolve items map.
 * Returns the `widgetSourceKey` of `source_viz` if set, otherwise the source key of the
 * on-page widget referenced by `source_widget_id`. Returns null if neither resolves.
 */
export function filterSourceKey(filter: PageFilter, widgets: PageWidget[]): string | null {
  if (filter.source_viz) return widgetSourceKey(filter.source_viz);
  if (!filter.source_widget_id) return null;
  const w = findContentWidgetById(widgets, filter.source_widget_id);
  return w ? widgetSourceKey(w.source) : null;
}

/**
 * A slicer whose source the viewer may not read: its options are that
 * source's data, so none are requested or shown. Returns the hub's message
 * for the source, or null when the slicer is usable. A date-range slicer
 * reads nothing and is never unavailable.
 */
export function slicerNotAvailable(
  filter: PageFilter,
  widgets: PageWidget[],
  errorsByKey: Record<string, { code: string; message: string } | undefined>
): { message: string } | null {
  if (isDateRangeSlicer(filter)) return null;
  const key = filterSourceKey(filter, widgets);
  if (!key) return null;
  const err = errorsByKey[key];
  return isNotAvailableError(err) ? { message: err!.message } : null;
}

export function isPageSlicerFilter(f: PageFilter): boolean {
  return (f.filter_kind ?? "slicer") === "slicer";
}

/**
 * Canonicalize a filter value so chart-click, row-select, slicer-option, and
 * row-cell values converge to a common form for `Set` membership checks.
 *
 * Date columns motivate this: ECharts may surface a JS `Date` (whose
 * `toString()` is `"Mon Jan 15 2024 …"`) while the table cell carries
 * `"2024-01-15"` or `"2024-01-15T00:00:00.000Z"`. Without canonicalization the
 * equality lookup fails and a chart selection filters the table to nothing.
 */
export function canonicalizeFilterValue(v: unknown): string {
  if (v == null) return "";
  if (v instanceof Date) {
    if (Number.isNaN(v.getTime())) return "";
    const y = v.getUTCFullYear();
    const m = String(v.getUTCMonth() + 1).padStart(2, "0");
    const d = String(v.getUTCDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }
  if (typeof v === "string") {
    if (/^\d{4}-\d{2}-\d{2}/.test(v)) return v.slice(0, 10);
    return v;
  }
  return String(v);
}

/** Emitted when TanStack row selection changes (page builder / live page). */
export type TanTableRowSelectEvent = { type: "sync"; selectedRows: Record<string, unknown>[] };

export function applyTanTableRowSelectToSelections(
  prev: Record<string, Set<string>>,
  pageFilters: PageFilter[],
  sourceWidgetId: string,
  event: TanTableRowSelectEvent
): Record<string, Set<string>> {
  const next = { ...prev };
  const matching = pageFilters.filter((f) => f.source_widget_id === sourceWidgetId);
  if (event.selectedRows.length === 0) {
    for (const f of matching) delete next[f.id];
    return next;
  }
  for (const f of matching) {
    const vals = event.selectedRows
      .map((r) => r[f.source_column])
      .filter((v) => v !== undefined && v !== null)
      .map((v) => canonicalizeFilterValue(v));
    if (vals.length === 0) {
      delete next[f.id];
    } else if (f.multi) {
      next[f.id] = new Set(vals);
    } else {
      next[f.id] = new Set([vals[vals.length - 1]!]);
    }
  }
  return next;
}

export type PageFilterRenderModel =
  | {
      kind: "echarts";
      option: unknown;
      height?: number;
      detailData?: DetailData;
      // ADR-0079/0080: typed-spec source so client surfaces (page, picker)
      // can recompile via `echartsSpecToOption` — `option.tooltip.formatter`
      // and friends are JS functions and don't survive JSON over the wire.
      spec?: EchartsVizSpecV1;
    }
  | {
      kind: "kpi";
      metrics: Array<{ label: string; formatted: string }>;
      style: Record<string, unknown>;
      detailData?: DetailData;
    }
  | {
      kind: "ai_content";
      contentType: "markdown" | "svg" | "html";
      content: string;
      title?: string;
      byteSize: number;
      sources?: Array<{ url: string; title: string }>;
    }
  | { kind: "ai_production_prompt"; text: string }
  | TanTableRenderModel;

function colIndexByName(columns: string[]): Map<string, number> {
  const map = new Map<string, number>();
  for (let idx = 0; idx < columns.length; idx++) {
    const column = columns[idx];
    const lower = column.toLowerCase();
    if (!map.has(lower)) map.set(lower, idx);
    const normalized = normalizeColumnName(column);
    if (normalized && !map.has(normalized)) map.set(normalized, idx);
  }
  return map;
}

function normalizeColumnName(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9]/g, "");
}

function extractStringDimensions(dataset: Record<string, unknown>): string[] {
  if (!Array.isArray(dataset.dimensions)) return [];
  return (dataset.dimensions as unknown[]).filter((dim): dim is string => typeof dim === "string");
}

function datasetRowsFromOption(option: Record<string, unknown>): unknown[][] {
  const ds = Array.isArray(option.dataset)
    ? (option.dataset[0] as Record<string, unknown> | undefined)
    : (option.dataset as Record<string, unknown> | undefined);
  if (!ds || !Array.isArray(ds.source)) return [];
  return ds.source as unknown[][];
}

function datasetColumnsFromOption(option: Record<string, unknown>): string[] {
  const ds = Array.isArray(option.dataset)
    ? (option.dataset[0] as Record<string, unknown> | undefined)
    : (option.dataset as Record<string, unknown> | undefined);
  if (!ds) return [];
  return extractStringDimensions(ds);
}

type ApplicableFilter = { column: string; values: Set<string>; matchNoRows?: boolean };

/** Impossible cell sentinel so legacy table runtimeFilters can mean "match zero rows". */
function valueAt(row: unknown, index: number, columnName: string): unknown {
  if (Array.isArray(row)) return row[index];
  if (row && typeof row === "object" && !Array.isArray(row)) {
    return (row as Record<string, unknown>)[columnName];
  }
  return undefined;
}

function filterRowsByColumns(rows: unknown[][], columns: string[], filters: ApplicableFilter[]): unknown[][] {
  const indices = colIndexByName(columns);
  const predicates = filters
    .map((filter) => {
      const lower = filter.column.toLowerCase();
      const normalized = normalizeColumnName(filter.column);
      const idx = indices.get(lower) ?? (normalized ? (indices.get(normalized) ?? -1) : -1);
      if (idx < 0) return null;
      const matchNoRows = "matchNoRows" in filter && filter.matchNoRows === true;
      return { idx, values: filter.values, column: columns[idx], matchNoRows };
    })
    .filter(
      (item): item is { idx: number; values: Set<string>; column: string; matchNoRows: boolean } => item !== null
    );

  if (predicates.length === 0) return rows;
  return rows.filter((row) =>
    predicates.every((predicate) => {
      if (predicate.matchNoRows) return false;
      return predicate.values.has(canonicalizeFilterValue(valueAt(row, predicate.idx, predicate.column)));
    })
  );
}

type DateRangeApplicableFilter = { column: string; start: string | null; end: string | null };

function filterRowsByDateRange(
  rows: unknown[][],
  columns: string[],
  filters: DateRangeApplicableFilter[]
): unknown[][] {
  const indices = colIndexByName(columns);
  const predicates = filters
    .map((filter) => {
      const lower = filter.column.toLowerCase();
      const normalized = normalizeColumnName(filter.column);
      const idx = indices.get(lower) ?? (normalized ? (indices.get(normalized) ?? -1) : -1);
      if (idx < 0) return null;
      return { idx, column: columns[idx], start: filter.start, end: filter.end };
    })
    .filter(
      (p): p is { idx: number; column: string; start: string | null; end: string | null } => p !== null
    );

  if (predicates.length === 0) return rows;
  return rows.filter((row) =>
    predicates.every((predicate) => {
      const rawVal = valueAt(row, predicate.idx, predicate.column);
      if (rawVal == null) return false;
      const cellStr = String(rawVal);
      const cellDate = cellStr.length >= 10 ? cellStr.slice(0, 10) : null;
      if (!cellDate) return false;
      if (predicate.start !== null && cellDate < predicate.start) return false;
      if (predicate.end !== null && cellDate > predicate.end) return false;
      return true;
    })
  );
}

function dateRangeFiltersForWidget(
  widgetId: string,
  activeFilters: PageFilter[],
  dateRangeSelections: DateRangeSelections
): DateRangeApplicableFilter[] {
  const out: DateRangeApplicableFilter[] = [];
  for (const filter of activeFilters) {
    if (!isDateRangeSlicer(filter)) continue;
    const state = dateRangeSelections[filter.id];
    if (!state || (state.start === null && state.end === null)) continue;
    const binding = filter.bindings.find((b) => b.widget_id === widgetId && b.kind === "column");
    if (!binding || binding.kind !== "column") continue;
    out.push({ column: binding.column, start: state.start, end: state.end });
  }
  return out;
}

function resolveXFieldIndexForDataset(
  option: Record<string, unknown>,
  columns: string[],
  datasetIndex: number
): number | null {
  const series = Array.isArray(option.series) ? option.series : [];
  for (const item of series) {
    if (typeof item !== "object" || item === null || Array.isArray(item)) continue;
    const seriesEntry = item as Record<string, unknown>;
    const ref = typeof seriesEntry.datasetIndex === "number" ? seriesEntry.datasetIndex : 0;
    if (ref !== datasetIndex) continue;
    const encode = typeof seriesEntry.encode === "object" && seriesEntry.encode !== null
      ? (seriesEntry.encode as Record<string, unknown>)
      : null;
    if (!encode) continue;
    const xName = typeof encode.x === "string" ? encode.x : null;
    if (!xName) continue;
    const idx = columns.findIndex((column) => column.toLowerCase() === xName.toLowerCase());
    if (idx >= 0) return idx;
  }
  return null;
}

export function clientAggregate(rows: unknown[][], keyColIdx: number, colCount: number): unknown[][] {
  if (rows.length === 0) return rows;
  const groups = new Map<unknown, unknown[][]>();
  for (const row of rows) {
    const key = row[keyColIdx];
    let group = groups.get(key);
    if (!group) {
      group = [];
      groups.set(key, group);
    }
    group.push(row);
  }

  let needsAggregate = false;
  for (const group of groups.values()) {
    if (group.length > 1) {
      needsAggregate = true;
      break;
    }
  }
  if (!needsAggregate) return rows;

  const out: unknown[][] = [];
  for (const [, group] of groups) {
    const aggregateRow: unknown[] = new Array(colCount);
    for (let c = 0; c < colCount; c++) {
      if (c === keyColIdx) {
        aggregateRow[c] = group[0][c];
        continue;
      }
      const allNum = group.every((r) => typeof r[c] === "number" && Number.isFinite(r[c] as number));
      aggregateRow[c] = allNum
        ? group.reduce((sum, r) => (sum as number) + (r[c] as number), 0 as number)
        : group[0][c];
    }
    out.push(aggregateRow);
  }
  return out;
}

export function getDetailOrFallback(rm: PageFilterRenderModel): { columns: string[]; rows: unknown[][] } {
  if (rm.kind === "tantable") return { columns: rm.columns.map((column) => column.key), rows: rm.rows };
  if (rm.kind === "kpi") {
    if (rm.detailData && rm.detailData.rows.length > 0) return rm.detailData;
    return { columns: [], rows: [] };
  }
  if (rm.kind === "ai_content" || rm.kind === "ai_production_prompt") return { columns: [], rows: [] };
  if (rm.kind === "echarts") {
    if (rm.detailData && rm.detailData.rows.length > 0) return rm.detailData;
    const option = rm.option as Record<string, unknown> | undefined;
    if (!option) return { columns: [], rows: [] };
    const columns = datasetColumnsFromOption(option);
    const rows = datasetRowsFromOption(option);
    return { columns, rows };
  }
  return { columns: [], rows: [] };
}

/**
 * Returns the distinct values for a column in first-occurrence order — the upstream
 * query's ORDER BY is the source of truth for slicer ordering. If you want a custom
 * sequence, sort it in the driving viz's SQL/transform.
 */
export function distinctValues(rm: PageFilterRenderModel, column: string): string[] {
  const detail = getDetailOrFallback(rm);
  const indices = colIndexByName(detail.columns);
  const colIdx = indices.get(column.toLowerCase()) ?? -1;
  if (colIdx < 0) return [];

  const seen = new Set<string>();
  const out: string[] = [];
  for (const row of detail.rows) {
    const val = valueAt(row, colIdx, detail.columns[colIdx]);
    if (val == null) continue;
    const key = canonicalizeFilterValue(val);
    if (key === "" || seen.has(key)) continue;
    seen.add(key);
    out.push(key);
  }
  return out;
}

/**
 * When the driver widget uses "filter unselected" mode, replace each filter's value set with
 * the complement (all distinct source values minus currently selected row values).
 */
export function effectiveFilterSelectionsForRender(
  activeFilters: PageFilter[],
  selections: Record<string, Set<string>>,
  driveUnselectedBySourceWidget: Record<string, boolean>,
  sourceRenderModelByFilterId: Record<string, PageFilterRenderModel | undefined>
): Record<string, Set<string>> {
  const out: Record<string, Set<string>> = {};
  for (const filter of activeFilters) {
    const base = selections[filter.id];
    // "drive unselected" only makes sense for on-page widget sources (tan table row selection).
    const unselectedMode =
      !!filter.source_widget_id && driveUnselectedBySourceWidget[filter.source_widget_id] === true;
    if (!unselectedMode) {
      if (base && base.size > 0) out[filter.id] = new Set(base);
      continue;
    }
    const rm = sourceRenderModelByFilterId[filter.id];
    if (!rm) continue;
    const all = distinctValues(rm, filter.source_column);
    if (!base || base.size === 0) {
      out[filter.id] = new Set(all);
    } else {
      const complement = all.filter((v) => !base.has(v));
      out[filter.id] = new Set(complement);
    }
  }
  return out;
}

function filtersForWidget(
  widgetId: string,
  activeFilters: PageFilter[],
  selections: Record<string, Set<string>>
): ApplicableFilter[] {
  const out: ApplicableFilter[] = [];
  for (const filter of activeFilters) {
    const selected = selections[filter.id];
    if (selected === undefined) continue;
    const binding = filter.bindings.find((item) => item.widget_id === widgetId && item.kind === "column");
    if (!binding || binding.kind !== "column") continue;
    if (selected.size === 0) {
      out.push({ column: binding.column, values: selected, matchNoRows: true });
      continue;
    }
    out.push({ column: binding.column, values: selected });
  }
  return out;
}

function materializeDataset(
  option: Record<string, unknown>,
  dataset: Record<string, unknown>,
  datasetIndex: number,
  detailColumns: string[],
  filters: ApplicableFilter[],
  dateRangeFilters: DateRangeApplicableFilter[] = []
): Record<string, unknown> {
  if (!Array.isArray(dataset.source)) return dataset;
  const datasetRows = dataset.source as unknown[][];
  const datasetColumns = (() => {
    const dimensions = extractStringDimensions(dataset);
    if (dimensions.length > 0) return dimensions;
    return detailColumns;
  })();

  const filteredRows = filterRowsByColumns(datasetRows, datasetColumns, filters);
  const dateFilteredRows = dateRangeFilters.length > 0
    ? filterRowsByDateRange(filteredRows, datasetColumns, dateRangeFilters)
    : filteredRows;
  const xIdx = resolveXFieldIndexForDataset(option, datasetColumns, datasetIndex);
  const aggregatedRows = xIdx !== null ? clientAggregate(dateFilteredRows, xIdx, datasetColumns.length) : dateFilteredRows;
  return {
    ...dataset,
    source: aggregatedRows,
    ...(datasetColumns.length > 0 ? { dimensions: datasetColumns } : {})
  };
}

export function filterRenderModel(
  rm: PageFilterRenderModel,
  widgetId: string,
  activeFilters: PageFilter[],
  selections: Record<string, Set<string>>,
  globalFilters: GlobalColumnFilter[] = [],
  dateRangeSelections: DateRangeSelections = {}
): PageFilterRenderModel {
  const applicableFilters = [
    ...filtersForWidget(widgetId, activeFilters, selections),
    ...globalFilters
  ];
  const dateRangeApplicable = dateRangeFiltersForWidget(widgetId, activeFilters, dateRangeSelections);
  if (applicableFilters.length === 0 && dateRangeApplicable.length === 0) return rm;

  const detail = getDetailOrFallback(rm);
  const listFilteredRows = filterRowsByColumns(detail.rows, detail.columns, applicableFilters);
  const filteredRows = dateRangeApplicable.length > 0
    ? filterRowsByDateRange(listFilteredRows, detail.columns, dateRangeApplicable)
    : listFilteredRows;

  if (rm.kind === "tantable") {
    const colKeys = rm.columns.map((c) => c.key);
    const listRows = filterRowsByColumns(rm.rows, colKeys, applicableFilters);
    const tanFilteredRows = dateRangeApplicable.length > 0
      ? filterRowsByDateRange(listRows, colKeys, dateRangeApplicable)
      : listRows;
    return { ...rm, rows: tanFilteredRows, rowCount: tanFilteredRows.length };
  }

  if (rm.kind === "ai_content" || rm.kind === "ai_production_prompt") {
    return rm;
  }

  if (rm.kind === "echarts") {
    const option = rm.option as Record<string, unknown> | undefined;
    if (!option) return rm;

    const datasets = Array.isArray(option.dataset)
      ? (option.dataset as Array<Record<string, unknown>>)
      : option.dataset
        ? [option.dataset as Record<string, unknown>]
        : [];

    if (datasets.length === 0) {
      const xIdx = resolveXFieldIndexForDataset(option, detail.columns, 0);
      const aggregatedRows = xIdx !== null ? clientAggregate(filteredRows, xIdx, detail.columns.length) : filteredRows;
      const nextOption = {
        ...option,
        dataset: {
          source: aggregatedRows,
          ...(detail.columns.length > 0 ? { dimensions: detail.columns } : {})
        }
      };
      return { ...rm, option: nextOption };
    }

    const rewritten = datasets.map((dataset, idx) =>
      materializeDataset(option, dataset, idx, detail.columns, applicableFilters, dateRangeApplicable)
    );
    const nextOption = { ...option, dataset: rewritten.length === 1 ? rewritten[0] : rewritten };
    return { ...rm, option: nextOption };
  }

  return rm;
}

/**
 * ADR-0079/0080: prefer client-side recompile from `renderModel.spec` so that
 * `tooltip.formatter`, `axisLabel.formatter`, etc. — which can't survive JSON
 * over the wire — are reconstructed in-process. Falls back to the wire `option`
 * for legacy/pre-spec render models or when the typed renderer rejects the spec.
 *
 * Recompile uses rows from `option.dataset[0].source` when present so that
 * filters applied by `filterRenderModel` (which writes into the option's
 * dataset, not into `detailData`) survive the recompile.
 */
export function resolveEchartsOption(
  rm: PageFilterRenderModel & { kind: "echarts" }
): EChartsOption {
  const fallback = (rm.option ?? {}) as EChartsOption;
  if (!rm.spec) return fallback;

  const opt = rm.option as Record<string, unknown> | undefined;
  const ds = opt
    ? Array.isArray(opt.dataset)
      ? (opt.dataset[0] as Record<string, unknown> | undefined)
      : (opt.dataset as Record<string, unknown> | undefined)
    : undefined;

  let rows: unknown[][] | undefined;
  let dimensions: string[] | undefined;
  if (ds && Array.isArray(ds.source)) {
    rows = ds.source as unknown[][];
    const dims = extractStringDimensions(ds);
    dimensions = dims.length > 0 ? dims : rm.detailData?.columns;
  } else if (rm.detailData) {
    rows = rm.detailData.rows;
    dimensions = rm.detailData.columns;
  }

  if (!rows || !dimensions || dimensions.length === 0) return fallback;
  const result = echartsSpecToOption(rm.spec, rows, dimensions);
  return result.ok ? result.option : fallback;
}
